import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea, Label } from "../components/ui/input";
import { ArrowLeft, Sparkles, CheckCircle2, Save, Download } from "lucide-react";
import { toast } from "sonner";
import { formatNum } from "../lib/utils";

export default function ReportDetail() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [editing, setEditing] = useState({});
  const [drafting, setDrafting] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/reports/${id}`);
    setReport(data);
  };
  useEffect(() => { load(); }, [id]);

  const save = async (i) => {
    await api.put(`/reports/${id}/section`, { section_index: i, content: editing[i] });
    toast.success("Section saved");
    setEditing((e) => ({ ...e, [i]: undefined }));
    load();
  };

  const aiDraft = async (i, heading) => {
    setDrafting(i);
    try {
      const { data } = await api.post("/ai/draft-disclosure", {
        framework_code: report.framework_code,
        section_heading: heading,
      });
      setEditing((e) => ({ ...e, [i]: data.draft }));
      toast.success("AI draft inserted — review and save");
    } catch (e) {
      toast.error("AI draft failed");
    } finally {
      setDrafting(null);
    }
  };

  const approve = async () => {
    await api.post(`/reports/${id}/approve`);
    toast.success("Report approved");
    load();
  };

  const downloadExport = async (fmt) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("lestari_token");
      const url = `${BACKEND_URL}/api/reports/${id}/export?fmt=${fmt}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const fname = m ? m[1] : `report.${fmt}.xhtml`;
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = fname;
      a.click();
      URL.revokeObjectURL(downloadUrl);
      toast.success(`Downloaded ${fname}`);
    } catch (e) {
      toast.error("Export failed: " + e.message);
    }
  };

  if (!report) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const e = report.emissions_summary || {};

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
      <div>
        <Link to="/reports" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3" data-testid="back-to-reports">
          <ArrowLeft className="h-3 w-3" /> Back to reports
        </Link>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow mb-1">{report.framework_name}</div>
            <h1 className="font-display text-3xl font-bold tracking-tight">{report.title}</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant={report.status === "draft" ? "warning" : "success"}>{report.status}</Badge>
              <span className="text-xs text-muted-foreground font-mono">FY{report.fiscal_year}</span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => downloadExport("pdf")} data-testid="export-pdf-btn"><Download className="h-4 w-4" /> PDF</Button>
            <Button variant="outline" onClick={() => downloadExport("ixbrl")} data-testid="export-ixbrl-btn"><Download className="h-4 w-4" /> iXBRL (SGX)</Button>
            <Button variant="outline" onClick={() => downloadExport("esef")} data-testid="export-esef-btn"><Download className="h-4 w-4" /> ESEF (EU)</Button>
            <Button variant="primary" onClick={approve} data-testid="approve-report-btn"><CheckCircle2 className="h-4 w-4" /> Approve</Button>
          </div>
        </div>
      </div>

      {/* Emissions snapshot */}
      <div className="grid grid-cols-4 gap-3">
        <SnapTile label="Scope 1" v={e.scope1} unit="tCO2e" />
        <SnapTile label="Scope 2" v={e.scope2} unit="tCO2e" />
        <SnapTile label="Scope 3" v={e.scope3} unit="tCO2e" />
        <SnapTile label="Total" v={e.total} unit="tCO2e" hi />
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {report.sections.map((s, i) => {
          const isEditing = editing[i] !== undefined;
          return (
            <div key={i} className="card-flat p-6" data-testid="report-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-semibold text-xl">{s.heading}</h3>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => aiDraft(i, s.heading)} disabled={drafting === i} data-testid="ai-draft-btn">
                    <Sparkles className={`h-3 w-3 ${drafting === i ? "animate-pulse" : ""}`} /> AI Draft
                  </Button>
                  {!isEditing ? (
                    <Button variant="ghost" size="sm" onClick={() => setEditing((e) => ({ ...e, [i]: s.content }))} data-testid="edit-section-btn">Edit</Button>
                  ) : (
                    <Button variant="primary" size="sm" onClick={() => save(i)} data-testid="save-section-btn"><Save className="h-3 w-3" /> Save</Button>
                  )}
                </div>
              </div>
              {isEditing ? (
                <Textarea
                  value={editing[i]}
                  onChange={(ev) => setEditing((e) => ({ ...e, [i]: ev.target.value }))}
                  rows={8}
                  className="font-sans"
                  data-testid="section-textarea"
                />
              ) : (
                <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">{s.content}</p>
              )}
            </div>
          );
        })}
      </div>

      {report.approvals && report.approvals.length > 0 && (
        <div className="card-flat p-6">
          <div className="label-eyebrow mb-3">Approvals</div>
          <div className="space-y-2">
            {report.approvals.map((a, i) => (
              <div key={i} className="text-sm font-mono flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                {a.by} ({a.role}) — {a.at}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SnapTile({ label, v, unit, hi }) {
  return (
    <div className={`card-flat p-4 ${hi ? "bg-accent/5 border-accent/30" : ""}`}>
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="font-display font-bold text-2xl">{formatNum(v, {decimals: 1})}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{unit}</div>
    </div>
  );
}
